import { priceImprovementBlotterApiConnector } from 'apiConnector/blotters/priceImprovementBlotter';
import { priceImprovementApi } from 'apiConnector/price-improvement';
import { quoteSensitivityApi } from 'apiConnector/quote/sensitivity';
import { isNavigatingInPriceImprovementBlotter } from 'epic/utils/navigation/navigation';
import { ofType } from 'redux-observable';
import { EMPTY, of, zip } from 'rxjs';
import { catchError, filter, ignoreElements, map, mergeMap, switchMap, take, withLatestFrom } from 'rxjs/operators';
import type { IPriceImprovementBlotterFilters } from 'state/blotters/priceImprovement';
import {
  initialPriceImprovementBlotterFilters,
  priceImprovementBlotterAcknowledgeFailed,
  priceImprovementBlotterAcknowledgeSucceeded,
  priceImprovementBlotterColumnDefinitionFailed,
  priceImprovementBlotterColumnDefinitionReceived,
  priceImprovementBlotterFiltersLoaded,
  priceImprovementBlotterFiltersLoadingFailed,
  priceImprovementBlotterPreferenceStateGet,
  priceImprovementSensitivityFailed,
  priceImprovementSensitivityReceived,
} from 'state/blotters/priceImprovement';
import type { EpicWithThunk } from 'state/state.common';
import { toApiError } from 'utils/http/apiErrorHelper';
import type { HttpClientError } from 'utils/http/httpClient.model';

export function fetchColumnDefinitionEpic(): EpicWithThunk {
  return (actions$, _state$, { httpClient }) =>
    actions$.pipe(
      ofType('PRICE_IMPROVEMENT_BLOTTER_FILTERS_LOADED'),
      mergeMap(_ => {
        return priceImprovementBlotterApiConnector(httpClient)
          .fetchColumnDefinition()
          .pipe(
            mergeMap(columns =>
              of(priceImprovementBlotterColumnDefinitionReceived(columns), priceImprovementBlotterPreferenceStateGet()),
            ),
            catchError(() => {
              return of(priceImprovementBlotterColumnDefinitionFailed());
            }),
          );
      }),
    );
}

export function persistFilters(): EpicWithThunk {
  return (
    action$,
    state$,
    {
      localStorageApiConnector,
      selectors: {
        priceImprovementBlotter: { getFiltersToPersist },
      },
    },
  ) =>
    action$.pipe(
      ofType('PRICE_IMPROVEMENT_BLOTTER_FILTER_CHANGED', 'PRICE_IMPROVEMENT_BLOTTER_ADVANCED_FILTERS_TOGGLED'),
      withLatestFrom(state$),
      switchMap(([, state]) => {
        const filters = getFiltersToPersist(state.blotters.priceImprovement);
        return localStorageApiConnector('price-improvement-blotter-filters', initialPriceImprovementBlotterFilters)
          .save<IPriceImprovementBlotterFilters>(filters)
          .pipe(
            ignoreElements(),
            catchError(() => {
              return EMPTY;
            }),
          );
      }),
    );
}

export function loadFilters(): EpicWithThunk {
  return (actions$, _state$, { localStorageApiConnector }) =>
    zip(
      actions$.pipe(ofType('USER_CURRENT_RECEIVED'), take(1)),
      actions$.pipe(filter(isNavigatingInPriceImprovementBlotter), take(1)),
    ).pipe(
      switchMap(_ => {
        return localStorageApiConnector('price-improvement-blotter-filters', initialPriceImprovementBlotterFilters)
          .load<IPriceImprovementBlotterFilters>()
          .pipe(
            mergeMap(response => [priceImprovementBlotterFiltersLoaded(response)]),
            catchError(() => {
              return of(priceImprovementBlotterFiltersLoadingFailed());
            }),
          );
      }),
    );
}

export function acknowledgePriceImprovementsSelectionEpic(): EpicWithThunk {
  return (
    actions$,
    state$,
    {
      httpClient,
      selectors: {
        priceImprovementBlotter: { getAcknowledgementRequest },
      },
    },
  ) => {
    const api = priceImprovementApi(httpClient);
    return actions$.pipe(
      filter(({ type }) => type === 'PRICE_IMPROVEMENT_BLOTTER_ACKNOWLEDGE_REQUESTED'),
      withLatestFrom(state$),
      switchMap(([, state]) => {
        const request = getAcknowledgementRequest(state);

        return api.acknowledge(request).pipe(
          map(() => {
            return priceImprovementBlotterAcknowledgeSucceeded();
          }),
          catchError((error: HttpClientError) => {
            return of(priceImprovementBlotterAcknowledgeFailed(toApiError(error)));
          }),
        );
      }),
    );
  };
}

export function fetchQuoteSensitivityEpic(): EpicWithThunk {
  return (actions$, state$, { httpClient }) => {
    const api = quoteSensitivityApi(httpClient);
    return actions$.pipe(
      ofType('PRICE_IMPROVEMENT_BLOTTER_DETAIL_OPENED'),
      withLatestFrom(state$),
      switchMap(
        ([
          {
            item: { id, referenceQuoteId },
          },
        ]) => {
          return api.getSensitivity(referenceQuoteId).pipe(
            map(sensitivity => {
              return priceImprovementSensitivityReceived(id, sensitivity);
            }),
            catchError(error => {
              return of(priceImprovementSensitivityFailed(id, error.message));
            }),
          );
        },
      ),
    );
  };
}
